import React, { ReactNode } from 'react';
import classNames from 'classnames/bind';

import { AccumServiceType, EnrichedAccumItemType } from 'types/accumulators';
import { UnlimThreeStatus } from 'constants/accumulators';
import { elkPlanBUnlimAccum3HoursEnabled } from 'utils/elkPlanBUnlimAccum3HoursEnabled';

import styles from './styles.pcss';
import { BaseListItem } from './BaseListItem';
import { GiftedListItem } from './GiftedListItem';

const cn = classNames.bind(styles);

type AccumulatorListProps = {
  accumulators: EnrichedAccumItemType[];
  children: (args: { accumulator: EnrichedAccumItemType; isInline: boolean }) => ReactNode;
  handleIconCloseClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  hasGiftAccum: boolean;
  hasTethering?: boolean;
  services?: AccumServiceType;
  showGiftAccum: boolean;
  tetheringIsLow?: boolean;
};

// As per Figma designs the amount of "multi line accumulators" is 2
// https://bwiki.beeline.ru/pages/viewpage.action?pageId=705310977
const MULTILINE_ACCUMULATORS_AMOUNT = 2;

const getIsInline = (itemIndex: number, hasTethering?: boolean) => {
  return hasTethering ? itemIndex >= 1 : itemIndex >= MULTILINE_ACCUMULATORS_AMOUNT;
};

const isUnlimitedWithServices = ({
  accumulator,
  services,
  tetheringIsLow,
}: {
  accumulator: EnrichedAccumItemType;
  services?: AccumServiceType;
  tetheringIsLow?: boolean;
}) => {
  const isUnlimitedTraffic = accumulator.unit === 'KBYTE' && accumulator.isUnlimited;
  const isElkUnlimAccum3HoursEnabled = elkPlanBUnlimAccum3HoursEnabled();
  const hasTrafficServices = !!(
    services?.tethering ||
    services?.unlimited24 ||
    (isElkUnlimAccum3HoursEnabled &&
      services?.unlimited3?.status === UnlimThreeStatus.ACTIVE &&
      !!services?.unlimited3?.expirationDate &&
      accumulator?.socType === 'PricePlan' &&
      accumulator?.unit === 'KBYTE')
  );
  const withLimitedTethering =
    typeof accumulator.tetheringRest === 'number' && typeof accumulator.tetheringSize === 'number';

  return isUnlimitedTraffic && (hasTrafficServices || withLimitedTethering || tetheringIsLow);
};

const hasClosedGiftedAccumulatorAfter = ({
  showGiftAccum,
  nextAccumulator,
}: {
  nextAccumulator?: EnrichedAccumItemType;
  showGiftAccum: boolean;
}) => {
  return nextAccumulator?.isGiftBucket && !showGiftAccum;
};

export const AccumulatorList: React.FC<AccumulatorListProps> = ({
  accumulators,
  showGiftAccum,
  handleIconCloseClick,
  hasGiftAccum,
  children,
  services,
  tetheringIsLow,
  hasTethering,
}) => {
  const baseClass = (hasGiftAccum && showGiftAccum) || !hasGiftAccum ? 'accumulator' : 'accum';

  return (
    <ul className={cn('wrap')}>
      {accumulators.map((accumulator, i) => (
        <>
          <BaseListItem
            baseClass="accum"
            isFullWidth={
              hasClosedGiftedAccumulatorAfter({
                nextAccumulator: accumulators[i + 1],
                showGiftAccum,
              }) || isUnlimitedWithServices({ accumulator, services, tetheringIsLow })
            }
            key={`${i.toString()}_base`}
            shouldRender={!accumulator?.isGiftBucket}
          >
            {children({ accumulator, isInline: getIsInline(i, hasTethering) })}
          </BaseListItem>
          <GiftedListItem
            baseClass={baseClass}
            handleIconCloseClick={handleIconCloseClick}
            hasTethering={hasTethering}
            key={`${i.toString()}_gifted`}
            rest={accumulator.rest}
            shouldRender={!!accumulator?.isGiftBucket && showGiftAccum}
          >
            {children({ accumulator, isInline: getIsInline(i, hasTethering) })}
          </GiftedListItem>
        </>
      ))}
    </ul>
  );
};
